"use client";

import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IResponsiveImages } from "@faire/web-api/indigofair/data/IResponsiveImages";
import { assignCSSVarsFromResponsiveProp } from "@faire/web/slate/VanillaExtract/dynamicUtils";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import React from "react";

import {
  SDUIImage,
  objectFitCoverImageStyles,
} from "@faire/retailer/components/SDUI/__internal__/Image/SDUIImage";
import { numberToPixel } from "@faire/retailer/components/SDUI/__internal__/Styling/conversions";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import { SDUIComponentRenderBehaviour } from "@faire/retailer/components/SDUI/__internal__/types";

import * as styles from "./style.css";
import { ImageTextBannerVariant } from "./types";

export const BannerImages = ({
  images,
  responsiveImages: serverResponsiveImages,
  variant,
  renderBehaviour,
}: {
  images: IImage[];
  responsiveImages?: IResponsiveImages;
  variant: ImageTextBannerVariant;
  renderBehaviour?: SDUIComponentRenderBehaviour;
}) => {
  const { isTabletAndAbove } = useViewport();

  if (variant === "left" && !isTabletAndAbove) {
    return null;
  }

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      serverResponsiveImages
    );

  if (responsiveImages.mobileAndAbove.length === 0) {
    return null;
  }

  const bannerImage = getResponsiveValueAtIndex(
    responsiveImages,
    0,
    IImage.build()
  );

  const tabletImageWidth =
    bannerImage.tabletAndAbove?.width ?? bannerImage.mobileAndAbove?.width;
  const desktopImageWidth =
    bannerImage.desktopAndAbove?.width ?? tabletImageWidth;

  const optimizeImageOptions = {
    mobileAndAbove: {
      height: styles.MOBILE_IMAGE_HEIGHT,
    },
    tabletAndAbove: {
      width: tabletImageWidth ?? styles.DESKTOP_IMAGE_WIDTH,
    },
    desktopAndAbove: {
      width: desktopImageWidth ?? styles.DESKTOP_IMAGE_WIDTH,
    },
  };

  return (
    <div
      className={styles.singleImage}
      style={assignInlineVars(
        assignCSSVarsFromResponsiveProp(
          {
            mobileAndAbove: "100%",
            tabletAndAbove: numberToPixel(tabletImageWidth) ?? "auto",
            desktopAndAbove: numberToPixel(desktopImageWidth) ?? "auto",
          },
          styles.varSingleBannerImageWidth
        )
      )}
    >
      <SDUIImage
        responsiveImage={bannerImage}
        optimizeImageOptions={optimizeImageOptions}
        imageStyles={objectFitCoverImageStyles}
        renderBehaviour={renderBehaviour}
      />
    </div>
  );
};
